import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import { 
  TrendingUp, Users, FileText, Target, Calendar, 
  CheckCircle, Clock, AlertCircle, Activity, Cpu, HardDrive,
  Server, Zap, Timer, Database, MemoryStick, Gauge
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const AnalyticsPage = () => {
  const { user } = useOutletContext();
  const [analytics, setAnalytics] = useState(null);
  const [consentsByPurpose, setConsentsByPurpose] = useState([]);
  const [consentsTrend, setConsentsTrend] = useState([]);
  const [campaignsData, setCampaignsData] = useState([]);
  const [groupsData, setGroupsData] = useState([]);
  const [performanceData, setPerformanceData] = useState(null);
  const [timeRange, setTimeRange] = useState('30');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
    fetchPerformanceMetrics();
    
    // Refresh performance metrics every 30 seconds
    const interval = setInterval(fetchPerformanceMetrics, 30000);
    return () => clearInterval(interval);
  }, [timeRange]);

  const fetchPerformanceMetrics = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/analytics/performance`, { withCredentials: true });
      setPerformanceData(response.data);
    } catch (error) {
      console.error('Error fetching performance metrics:', error);
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [analyticsRes, consentsRes, campaignsRes, groupsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/analytics/dashboard`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/consents`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/marketing-campaigns`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/titular-groups`, { withCredentials: true })
      ]);

      setAnalytics(analyticsRes.data);
      
      // Process consents by purpose
      const consents = Array.isArray(consentsRes.data) ? consentsRes.data : [];
      const purposeCount = {};
      consents.forEach(consent => {
        (consent.purposes || []).forEach(purpose => {
          purposeCount[purpose] = (purposeCount[purpose] || 0) + 1;
        });
      });
      
      const purposeLabels = {
        newsletter: 'Newsletter',
        promotions: 'Promoções',
        events: 'Eventos',
        surveys: 'Inquéritos',
        product_updates: 'Atualizações'
      };
      
      setConsentsByPurpose(
        Object.entries(purposeCount).map(([key, value]) => ({
          name: purposeLabels[key] || key,
          value,
          fill: COLORS[Object.keys(purposeCount).indexOf(key) % COLORS.length]
        }))
      );

      // Process consents trend (last N days)
      const days = parseInt(timeRange);
      const trendData = [];
      const now = new Date();
      
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        
        const dayConsents = consents.filter(c => {
          const consentDate = new Date(c.created_at).toISOString().split('T')[0];
          return consentDate === dateStr;
        });
        
        trendData.push({
          date: date.toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' }),
          novos: dayConsents.length,
          ativos: dayConsents.filter(c => c.status === 'active').length,
          revogados: dayConsents.filter(c => c.status === 'revoked').length
        });
      }
      setConsentsTrend(trendData);

      // Process campaigns
      const campaigns = Array.isArray(campaignsRes.data) ? campaignsRes.data : [];
      const statusCount = {
        draft: 0,
        approved: 0,
        executed: 0
      };
      campaigns.forEach(c => {
        if (c.status === 'executed') statusCount.executed++;
        else if (c.dpo_approved) statusCount.approved++;
        else statusCount.draft++;
      });
      
      setCampaignsData([
        { name: 'Rascunho', value: statusCount.draft, fill: '#94A3B8' },
        { name: 'Aprovadas', value: statusCount.approved, fill: '#10B981' },
        { name: 'Executadas', value: statusCount.executed, fill: '#3B82F6' }
      ]);

      // Process groups
      const groups = Array.isArray(groupsRes.data) ? groupsRes.data : [];
      setGroupsData(
        groups.map(g => ({
          name: g.name.length > 15 ? g.name.substring(0, 15) + '...' : g.name,
          membros: g.titular_count || 0,
          fill: g.color || '#3B82F6'
        }))
      );

    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Erro ao carregar analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const totalConsents = analytics?.consents?.total || 0;
  const activeRate = totalConsents > 0 ? Math.round(((analytics?.consents?.active || 0) / totalConsents) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Analytics & Relatórios</h1>
          <p className="text-slate-500 mt-1">Visão geral do cumprimento RGPD</p>
        </div>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Período" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Últimos 7 dias</SelectItem>
            <SelectItem value="30">Últimos 30 dias</SelectItem>
            <SelectItem value="90">Últimos 90 dias</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Consentimentos</p>
                <p className="text-3xl font-bold">{totalConsents}</p>
                <p className="text-xs text-slate-400 mt-1">
                  <TrendingUp className="w-3 h-3 inline mr-1 text-green-500" />
                  {activeRate}% ativos
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Taxa de Compliance</p>
                <p className="text-3xl font-bold text-green-600">{activeRate}%</p>
                <p className="text-xs text-slate-400 mt-1">
                  Consentimentos válidos
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Campanhas</p>
                <p className="text-3xl font-bold text-purple-600">
                  {campaignsData.reduce((sum, c) => sum + c.value, 0)}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {campaignsData.find(c => c.name === 'Executadas')?.value || 0} executadas
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <Target className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Grupos</p>
                <p className="text-3xl font-bold">{groupsData.length}</p>
                <p className="text-xs text-slate-400 mt-1">
                  {groupsData.reduce((sum, g) => sum + g.membros, 0)} membros total
                </p>
              </div>
              <div className="p-3 bg-amber-100 rounded-full">
                <Users className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Consents Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Evolução de Consentimentos
            </CardTitle>
            <CardDescription>Novos consentimentos nos últimos {timeRange} dias</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={consentsTrend}>
                <defs>
                  <linearGradient id="colorNovos" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94A3B8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px'
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="novos" 
                  stroke="#3B82F6" 
                  fillOpacity={1} 
                  fill="url(#colorNovos)"
                  name="Novos"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Consents by Purpose */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Consentimentos por Finalidade
            </CardTitle>
            <CardDescription>Distribuição das finalidades de marketing</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={consentsByPurpose}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {consentsByPurpose.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Campaigns Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Estado das Campanhas
            </CardTitle>
            <CardDescription>Distribuição por estado de aprovação</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={campaignsData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94A3B8" />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} stroke="#94A3B8" width={80} />
                <Tooltip />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {campaignsData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Groups Members */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Membros por Grupo
            </CardTitle>
            <CardDescription>Distribuição de titulares por grupo</CardDescription>
          </CardHeader>
          <CardContent>
            {groupsData.length === 0 ? (
              <div className="flex items-center justify-center h-[250px] text-slate-400">
                Nenhum grupo criado
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={groupsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="#94A3B8" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8" />
                  <Tooltip />
                  <Bar dataKey="membros" radius={[4, 4, 0, 0]}>
                    {groupsData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Consent Status Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Resumo de Conformidade RGPD</CardTitle>
          <CardDescription>Visão geral do estado dos consentimentos</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-green-600" />
                <div>
                  <p className="text-2xl font-bold text-green-600">{analytics?.consents?.active || 0}</p>
                  <p className="text-sm text-green-700">Consentimentos Ativos</p>
                  <p className="text-xs text-green-600 mt-1">Podem receber comunicações de marketing</p>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 text-amber-600" />
                <div>
                  <p className="text-2xl font-bold text-amber-600">{analytics?.consents?.pending || 0}</p>
                  <p className="text-sm text-amber-700">Consentimentos Pendentes</p>
                  <p className="text-xs text-amber-600 mt-1">Aguardam validação do titular</p>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-8 h-8 text-red-600" />
                <div>
                  <p className="text-2xl font-bold text-red-600">{analytics?.consents?.revoked || 0}</p>
                  <p className="text-sm text-red-700">Consentimentos Revogados</p>
                  <p className="text-xs text-red-600 mt-1">Não podem ser contactados</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Performance Metrics Section */}
      {performanceData && (
        <div className="space-y-6 mt-8" data-testid="performance-metrics">
          <div className="border-t pt-6">
            <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2 mb-4">
              <Gauge className="w-6 h-6" />
              Métricas de Performance
            </h2>
            <p className="text-slate-500 mb-6">Monitorização de recursos do sistema e tempos de resposta da API</p>
          </div>

          {/* Alerts Section */}
          {performanceData.alerts && performanceData.alerts.length > 0 && (
            <Card className="border-l-4 border-l-red-500 bg-red-50">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-5 h-5" />
                  Alertas Ativos ({performanceData.alerts.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {performanceData.alerts.map((alert, index) => (
                    <div key={index} className={`p-3 rounded-lg flex items-center justify-between ${
                      alert.severity === 'critical' ? 'bg-red-100 border border-red-200' : 'bg-amber-100 border border-amber-200'
                    }`}>
                      <div className="flex items-center gap-2">
                        <AlertCircle className={`w-4 h-4 ${alert.severity === 'critical' ? 'text-red-600' : 'text-amber-600'}`} />
                        <span className={`font-medium ${alert.severity === 'critical' ? 'text-red-800' : 'text-amber-800'}`}>
                          {alert.message}
                        </span>
                      </div>
                      <Badge variant={alert.severity === 'critical' ? 'destructive' : 'warning'}>
                        {alert.severity === 'critical' ? 'Crítico' : 'Aviso'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Health Status Banner */}
          {performanceData.health_status && performanceData.health_status !== 'healthy' && (
            <div className={`p-4 rounded-lg flex items-center gap-3 ${
              performanceData.health_status === 'critical' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
            }`}>
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">
                Estado do Sistema: {performanceData.health_status === 'critical' ? 'Crítico - Ação Imediata Necessária' : 'Degradado - Monitorização Recomendada'}
              </span>
            </div>
          )}

          {/* Performance KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">CPU</p>
                    <p className="text-3xl font-bold">{performanceData.system?.cpu_percent || 0}%</p>
                    <Progress 
                      value={performanceData.system?.cpu_percent || 0} 
                      className="mt-2 h-2"
                    />
                  </div>
                  <div className={`p-3 rounded-full ${performanceData.system?.cpu_percent > 80 ? 'bg-red-100' : 'bg-blue-100'}`}>
                    <Cpu className={`w-6 h-6 ${performanceData.system?.cpu_percent > 80 ? 'text-red-600' : 'text-blue-600'}`} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Memória RAM</p>
                    <p className="text-3xl font-bold">{performanceData.system?.memory?.percent || 0}%</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {performanceData.system?.memory?.used_gb || 0}GB / {performanceData.system?.memory?.total_gb || 0}GB
                    </p>
                    <Progress 
                      value={performanceData.system?.memory?.percent || 0} 
                      className="mt-2 h-2"
                    />
                  </div>
                  <div className={`p-3 rounded-full ${performanceData.system?.memory?.percent > 85 ? 'bg-red-100' : 'bg-green-100'}`}>
                    <MemoryStick className={`w-6 h-6 ${performanceData.system?.memory?.percent > 85 ? 'text-red-600' : 'text-green-600'}`} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Disco</p>
                    <p className="text-3xl font-bold">{performanceData.system?.disk?.percent || 0}%</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {performanceData.system?.disk?.free_gb || 0}GB livres
                    </p>
                    <Progress 
                      value={performanceData.system?.disk?.percent || 0} 
                      className="mt-2 h-2"
                    />
                  </div>
                  <div className={`p-3 rounded-full ${performanceData.system?.disk?.percent > 90 ? 'bg-red-100' : 'bg-purple-100'}`}>
                    <HardDrive className={`w-6 h-6 ${performanceData.system?.disk?.percent > 90 ? 'text-red-600' : 'text-purple-600'}`} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Tempo de Resposta</p>
                    <p className="text-3xl font-bold">{performanceData.response_times?.avg_1h_ms || 0}ms</p>
                    <p className="text-xs text-slate-400 mt-1">
                      Média última hora ({performanceData.response_times?.requests_1h || 0} req)
                    </p>
                  </div>
                  <div className={`p-3 rounded-full ${performanceData.response_times?.avg_1h_ms > 500 ? 'bg-amber-100' : 'bg-teal-100'}`}>
                    <Timer className={`w-6 h-6 ${performanceData.response_times?.avg_1h_ms > 500 ? 'text-amber-600' : 'text-teal-600'}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Response Times & Slowest Endpoints */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5" />
                  Tempos de Resposta
                </CardTitle>
                <CardDescription>Estatísticas de latência da API</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded">
                        <Clock className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium">Média (última hora)</p>
                        <p className="text-xs text-slate-500">{performanceData.response_times?.requests_1h || 0} requisições</p>
                      </div>
                    </div>
                    <Badge variant={performanceData.response_times?.avg_1h_ms < 200 ? "success" : performanceData.response_times?.avg_1h_ms < 500 ? "warning" : "destructive"}>
                      {performanceData.response_times?.avg_1h_ms || 0}ms
                    </Badge>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded">
                        <Activity className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium">Média (últimas 24h)</p>
                        <p className="text-xs text-slate-500">{performanceData.response_times?.requests_24h || 0} requisições</p>
                      </div>
                    </div>
                    <Badge variant={performanceData.response_times?.avg_24h_ms < 200 ? "success" : performanceData.response_times?.avg_24h_ms < 500 ? "warning" : "destructive"}>
                      {performanceData.response_times?.avg_24h_ms || 0}ms
                    </Badge>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded">
                        <Server className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium">Memória do Processo</p>
                        <p className="text-xs text-slate-500">{performanceData.process?.threads || 0} threads ativas</p>
                      </div>
                    </div>
                    <Badge variant="outline">
                      {performanceData.process?.memory_mb || 0}MB
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  Endpoints Mais Lentos
                </CardTitle>
                <CardDescription>Top 5 endpoints com maior latência (24h)</CardDescription>
              </CardHeader>
              <CardContent>
                {performanceData.slowest_endpoints?.length > 0 ? (
                  <div className="space-y-3">
                    {performanceData.slowest_endpoints.map((endpoint, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            index === 0 ? 'bg-red-100 text-red-700' : 
                            index === 1 ? 'bg-amber-100 text-amber-700' : 
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {index + 1}
                          </span>
                          <div>
                            <p className="font-mono text-sm truncate max-w-[200px]">{endpoint.endpoint}</p>
                            <p className="text-xs text-slate-500">{endpoint.count} chamadas</p>
                          </div>
                        </div>
                        <Badge variant={endpoint.avg_ms < 200 ? "outline" : endpoint.avg_ms < 500 ? "warning" : "destructive"}>
                          {endpoint.avg_ms}ms
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-[200px] text-slate-400">
                    <div className="text-center">
                      <Zap className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>Sem dados de endpoints ainda</p>
                      <p className="text-xs">Os dados aparecerão após algumas requisições</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* System Health Status */}
          <Card>
            <CardHeader>
              <CardTitle>Estado de Saúde do Sistema</CardTitle>
              <CardDescription>Última atualização: {performanceData.timestamp ? new Date(performanceData.timestamp).toLocaleString('pt-PT') : 'N/A'}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className={`p-4 rounded-lg border ${
                  performanceData.system?.cpu_percent < 70 ? 'bg-green-50 border-green-200' : 
                  performanceData.system?.cpu_percent < 85 ? 'bg-amber-50 border-amber-200' : 
                  'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Cpu className="w-4 h-4" />
                    <span className="text-sm font-medium">CPU</span>
                  </div>
                  <Badge variant={performanceData.system?.cpu_percent < 70 ? "success" : performanceData.system?.cpu_percent < 85 ? "warning" : "destructive"}>
                    {performanceData.system?.cpu_percent < 70 ? 'Normal' : performanceData.system?.cpu_percent < 85 ? 'Elevado' : 'Crítico'}
                  </Badge>
                </div>
                
                <div className={`p-4 rounded-lg border ${
                  performanceData.system?.memory?.percent < 75 ? 'bg-green-50 border-green-200' : 
                  performanceData.system?.memory?.percent < 90 ? 'bg-amber-50 border-amber-200' : 
                  'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <MemoryStick className="w-4 h-4" />
                    <span className="text-sm font-medium">Memória</span>
                  </div>
                  <Badge variant={performanceData.system?.memory?.percent < 75 ? "success" : performanceData.system?.memory?.percent < 90 ? "warning" : "destructive"}>
                    {performanceData.system?.memory?.percent < 75 ? 'Normal' : performanceData.system?.memory?.percent < 90 ? 'Elevado' : 'Crítico'}
                  </Badge>
                </div>
                
                <div className={`p-4 rounded-lg border ${
                  performanceData.system?.disk?.percent < 80 ? 'bg-green-50 border-green-200' : 
                  performanceData.system?.disk?.percent < 95 ? 'bg-amber-50 border-amber-200' : 
                  'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <HardDrive className="w-4 h-4" />
                    <span className="text-sm font-medium">Disco</span>
                  </div>
                  <Badge variant={performanceData.system?.disk?.percent < 80 ? "success" : performanceData.system?.disk?.percent < 95 ? "warning" : "destructive"}>
                    {performanceData.system?.disk?.percent < 80 ? 'Normal' : performanceData.system?.disk?.percent < 95 ? 'Atenção' : 'Crítico'}
                  </Badge>
                </div>
                
                <div className={`p-4 rounded-lg border ${
                  performanceData.response_times?.avg_1h_ms < 300 ? 'bg-green-50 border-green-200' : 
                  performanceData.response_times?.avg_1h_ms < 600 ? 'bg-amber-50 border-amber-200' : 
                  'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-4 h-4" />
                    <span className="text-sm font-medium">API</span>
                  </div>
                  <Badge variant={performanceData.response_times?.avg_1h_ms < 300 ? "success" : performanceData.response_times?.avg_1h_ms < 600 ? "warning" : "destructive"}>
                    {performanceData.response_times?.avg_1h_ms < 300 ? 'Rápida' : performanceData.response_times?.avg_1h_ms < 600 ? 'Lenta' : 'Crítico'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AnalyticsPage;
